// Modal com a análise completa de uma reunião do Memor.IA (visão adm).
// Busca /api/reunioes/detalhe/<codigo>/ e mostra a reunião + a avaliação de cada operador.
// `lateral` (opcional) é renderizado numa coluna à direita — usado pela página de
// contestações para mostrar a análise lado a lado com as contestações e o prompt.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { LABEL_COMPETENCIA, labelTipoReuniao } from '../utils/competencias';

const API = process.env.REACT_APP_API_BASE;

const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};

const badgeNota = (nota) => {
  const n = parseFloat(nota) || 0;
  if (n >= 8) return 'bg-success';
  if (n >= 6) return 'bg-primary';
  if (n >= 4) return 'bg-warning text-dark';
  return 'bg-danger';
};

const textoItem = (x, ...campos) => {
  if (typeof x === 'string') return x;
  for (const c of campos) if (x && x[c]) return x[c];
  return '';
};

const Rotulo = ({ children }) => (
  <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>{children}</div>
);

function Palavras({ titulo, lista, positivo }) {
  if (!Array.isArray(lista) || lista.length === 0) return null;
  return (
    <div className="mb-2">
      <Rotulo>{titulo}</Rotulo>
      <div className="d-flex flex-wrap gap-1 mt-1">
        {lista.map((p, i) => (
          <span key={i} className="badge" style={{
            background: positivo ? '#d1e7dd' : '#f8d7da',
            color: positivo ? '#0f5132' : '#842029',
            fontSize: '0.74rem', fontWeight: 500,
          }}>{textoItem(p, 'palavra', 'termo', 'texto')}</span>
        ))}
      </div>
    </div>
  );
}

function ConteudoAnalise({ r, ops }) {
  return (
    <>
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3 text-center">
          <small className="text-muted d-block">Nota da reunião</small>
          <span className={`badge fs-6 ${badgeNota(r.nota)}`}>{r.nota != null ? `${r.nota}/10` : '—'}</span>
        </div>
        <div className="col-6 col-md-3 text-center">
          <small className="text-muted d-block">Tipo</small>
          <span className="badge bg-secondary">{labelTipoReuniao(r.tipo_reuniao)}</span>
        </div>
        <div className="col-6 col-md-3 text-center">
          <small className="text-muted d-block">Data</small>
          <strong style={{ fontSize: '0.9rem' }}>{fmtData(r.data_reuniao)}{r.horario ? ` · ${r.horario}` : ''}</strong>
        </div>
        <div className="col-6 col-md-3 text-center">
          <small className="text-muted d-block">Classificação</small>
          <span className={`badge ${
            r.classificacao === 'muito_boa' ? 'bg-success'
              : r.classificacao === 'boa' ? 'bg-primary'
                : r.classificacao === 'critica' ? 'bg-danger' : 'bg-secondary'
          }`}>{(r.classificacao || '-').replace('_', ' ').toUpperCase()}</span>
        </div>
      </div>

      {r.analisado_por && (
        <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
          <i className="fas fa-user-check me-1"></i>Analisada por <strong>{r.analisado_por}</strong>
        </div>
      )}
      {r.resumo && (
        <div className="card border-info mb-3">
          <div className="card-body py-2">
            <h6 className="text-info mb-1"><i className="fas fa-file-alt me-1"></i> Resumo</h6>
            <p className="mb-0" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{r.resumo}</p>
          </div>
        </div>
      )}

      {(r.cliente_nome || r.interesse_nivel || r.cliente_orcamento) && (
        <div className="d-flex flex-wrap gap-3 mb-3">
          {r.cliente_nome && (<div><Rotulo>CONTATO</Rotulo><div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{r.cliente_nome}{r.cliente_cargo ? ` · ${r.cliente_cargo}` : ''}</div></div>)}
          {r.interesse_nivel && (<div><Rotulo>INTERESSE</Rotulo><div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{r.interesse_nivel}</div></div>)}
          {r.estagio_venda && r.estagio_venda !== 'não se aplica' && (<div><Rotulo>ESTÁGIO</Rotulo><div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{r.estagio_venda}</div></div>)}
          {r.cliente_orcamento && (<div><Rotulo>ORÇAMENTO</Rotulo><div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{r.cliente_orcamento}</div></div>)}
        </div>
      )}

      {r.cliente_dor_principal && (
        <div className="alert py-2 px-3 mb-3" style={{ background: '#fff4e0', borderLeft: '4px solid #fd7e14' }}>
          <strong style={{ fontSize: '0.78rem' }}>DOR PRINCIPAL</strong>
          <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{r.cliente_dor_principal}</p>
        </div>
      )}
      {r.oportunidade && (
        <div className="alert alert-success py-2 px-3 mb-3">
          <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-lightbulb me-1"></i>OPORTUNIDADE</strong>
          <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{r.oportunidade}</p>
        </div>
      )}

      {(r.pontos_criticos || []).length > 0 && (
        <div className="mb-3">
          <h6 className="fw-bold mb-2"><i className="fas fa-triangle-exclamation me-2 text-danger"></i>Pontos críticos e oportunidades</h6>
          {r.pontos_criticos.map((p, i) => {
            const ehRisco = (p?.tipo || '') === 'risco';
            return (
              <div key={i} className="d-flex gap-2 mb-2 p-2 rounded" style={{ background: ehRisco ? '#fdecec' : '#eaf7ef', fontSize: '0.87rem' }}>
                <span className="badge flex-shrink-0" style={{ background: ehRisco ? '#dc3545' : '#198754', height: 'fit-content' }}>{ehRisco ? 'Risco' : 'Oportunidade'}</span>
                <span>{textoItem(p, 'descricao')}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="row g-3 mb-3">
        {(r.objecoes || []).length > 0 && (
          <div className="col-md-6">
            <h6 className="fw-bold mb-2"><i className="fas fa-shield-halved me-2 text-primary"></i>Objeções</h6>
            {r.objecoes.map((o, i) => (
              <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.87rem' }}>
                <i className="fas fa-circle-dot flex-shrink-0 text-primary" style={{ marginTop: 5, fontSize: '0.6rem' }}></i>
                <span>{textoItem(o, 'objecao', 'descricao')}</span>
              </div>
            ))}
          </div>
        )}
        {(r.proximos_passos || []).length > 0 && (
          <div className="col-md-6">
            <h6 className="fw-bold mb-2"><i className="fas fa-list-check me-2 text-success"></i>Próximos passos</h6>
            {r.proximos_passos.map((p, i) => {
              const texto = typeof p === 'string' ? p
                : [p?.responsavel, p?.acao].filter(Boolean).join(' — ') + (p?.prazo ? ` (até ${p.prazo})` : '');
              return (
                <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.87rem' }}>
                  <i className="fas fa-arrow-right flex-shrink-0 text-success" style={{ marginTop: 4, fontSize: '0.72rem' }}></i>
                  <span>{texto}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Palavras titulo="PALAVRAS POSITIVAS" lista={r.palavras_positivas} positivo />
      <Palavras titulo="PALAVRAS NEGATIVAS" lista={r.palavras_negativas} />

      <h6 className="fw-bold mb-2 mt-4"><i className="fas fa-users me-2 text-muted"></i>Análise por operador ({ops.length})</h6>
      {ops.length === 0 && <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Nenhuma análise individual registrada.</div>}
      {ops.map((op, i) => (
        <div key={i} className="card mb-3" style={{ borderLeft: '4px solid #00205b' }}>
          <div className="card-body py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: '0.95rem' }}><i className="fas fa-user-circle me-2 text-muted"></i>{op.operador}</strong>
              <span className={`badge ${badgeNota(op.nota)}`}>{op.nota != null ? `${op.nota}/10` : '—'}</span>
            </div>
            {op.ponto_forte && (
              <div className="mb-1" style={{ fontSize: '0.85rem' }}>
                <i className="fas fa-thumbs-up text-success me-2"></i>{op.ponto_forte}
              </div>
            )}
            {op.ponto_melhoria && (
              <div className="mb-2" style={{ fontSize: '0.85rem' }}>
                <i className="fas fa-arrow-up text-danger me-2"></i>{op.ponto_melhoria}
              </div>
            )}
            {(op.competencias || []).filter((c) => c && c.competencia).length > 0 && (
              <div className="d-flex flex-column gap-1 mt-2">
                {op.competencias.filter((c) => c && c.competencia).map((c, j) => {
                  const naoAplica = c.status === 'nao_se_aplica';
                  const demonstrou = c.status === 'demonstrou';
                  return (
                    <div key={j} className="p-2 rounded" style={{
                      background: naoAplica ? '#f8f9fa' : demonstrou ? '#eaf7ef' : '#fdecec', fontSize: '0.8rem',
                    }}>
                      <span className="fw-semibold" style={{ color: naoAplica ? '#6c757d' : demonstrou ? '#0f5132' : '#842029' }}>
                        {LABEL_COMPETENCIA[c.competencia] || c.competencia}
                      </span>
                      <span className="text-muted">
                        {' · '}{naoAplica ? 'não se aplica' : demonstrou ? 'demonstrou' : 'não demonstrou'}
                        {!naoAplica && c.nota !== null && c.nota !== undefined && <> · nota {c.nota}</>}
                      </span>
                      {c.evidencia && <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>{c.evidencia}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ))}

      {r.observacoes && (
        <div className="p-3 rounded-3" style={{ background: '#f8f9fa', fontSize: '0.87rem' }}>
          <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-robot me-1"></i> OBSERVAÇÕES DA MEMOR.IA</strong>
          <p className="mb-0 mt-1 fst-italic text-muted">{r.observacoes}</p>
        </div>
      )}
    </>
  );
}

export default function ReuniaoAnaliseModal({ codigo, onClose, lateral = null }) {
  const [estado, setEstado] = useState({ carregando: true });

  useEffect(() => {
    if (!codigo) return undefined;
    let ativo = true;
    setEstado({ carregando: true });
    axios.get(`${API}/api/reunioes/detalhe/${encodeURIComponent(codigo)}/`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        if (!ativo) return;
        if (res.data?.success) setEstado({ reuniao: res.data.reuniao, operadores: res.data.operadores || [] });
        else setEstado({ erro: res.data?.erro || 'Não foi possível carregar a análise.' });
      })
      .catch((e) => { if (ativo) setEstado({ erro: e.response?.data?.erro || 'Não foi possível carregar a análise.' }); });
    return () => { ativo = false; };
  }, [codigo]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  if (!codigo) return null;
  const r = estado.reuniao;

  // Portal no <body>: dentro do card o modal ficava preso à área dele (cortado e com rolagem)
  return createPortal(
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1250 }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-scrollable"
        style={{ width: '95vw', maxWidth: lateral ? '1600px' : '1400px', margin: '2.5vh auto', height: '95vh' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-content" style={{ height: '95vh', maxHeight: '95vh' }}>
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-video me-2 text-muted"></i>
                {codigo}{r && (r.cliente_empresa || r.assunto) ? ` — ${r.cliente_empresa || r.assunto}` : ''}
              </h5>
              {r && r.assunto && r.cliente_empresa && (
                <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>{r.assunto}</div>
              )}
            </div>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ flex: '1 1 auto', overflowY: 'auto' }}>
            <div className="row g-4">
              <div className={lateral ? 'col-lg-7' : 'col-12'}>
                {estado.carregando && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                    <p className="text-muted mb-0">Carregando a análise completa...</p>
                  </div>
                )}
                {estado.erro && <div className="alert alert-warning mb-0">{estado.erro}</div>}
                {r && <ConteudoAnalise r={r} ops={estado.operadores || []} />}
              </div>
              {lateral && (
                <div className="col-lg-5">
                  <div style={{ position: 'sticky', top: 0 }}>{lateral}</div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-1"></i>Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}
